/* ============================================================================
   deSal ELEVATE — interaction layer
   Vanilla, dependency-free, rAF-throttled, fully guarded by prefers-reduced-motion.
   Re-runs on Shopify section render (theme editor) and is idempotent.
   ============================================================================ */
(function () {
  'use strict';

  var REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var FINE_POINTER = window.matchMedia('(pointer: fine)').matches;

  /* ---------- helpers ---------- */
  function rafThrottle(fn) {
    var queued = false, lastArgs;
    return function () {
      lastArgs = arguments;
      if (queued) return;
      queued = true;
      requestAnimationFrame(function () {
        queued = false;
        fn.apply(null, lastArgs);
      });
    };
  }
  function clamp(v, a, b) { return Math.max(a, Math.min(b, v)); }

  /* ========================================================================
     1. SCROLL REVEAL (element-level)
     ======================================================================== */
  function initReveal(root) {
    var els = (root || document).querySelectorAll('[data-ds-reveal], [data-ds-stagger]');
    if (!els.length) return;
    if (REDUCED) {
      els.forEach(function (el) { el.classList.add('ds-in'); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('ds-in');
          io.unobserve(entry.target);
        }
      });
    }, { threshold: [0, 0.12], rootMargin: '0px 0px -8% 0px' });
    els.forEach(function (el) {
      if (!el.classList.contains('ds-in')) io.observe(el);
    });
  }

  /* ========================================================================
     2. COUNT-UP
     ======================================================================== */
  function animateCount(el) {
    var target = parseFloat(el.getAttribute('data-ds-count'));
    if (isNaN(target)) return;
    var suffix = el.getAttribute('data-ds-count-suffix') || '';
    var prefix = el.getAttribute('data-ds-count-prefix') || '';
    var decimals = (String(target).split('.')[1] || '').length;
    if (REDUCED) { el.textContent = prefix + target.toFixed(decimals) + suffix; return; }
    var dur = 1500, start = null;
    function step(ts) {
      if (start === null) start = ts;
      var p = clamp((ts - start) / dur, 0, 1);
      var eased = 1 - Math.pow(1 - p, 3); /* easeOutCubic */
      el.textContent = prefix + (target * eased).toFixed(decimals) + suffix;
      if (p < 1) requestAnimationFrame(step);
      else el.textContent = prefix + target.toFixed(decimals) + suffix;
    }
    requestAnimationFrame(step);
  }
  function initCounts(root) {
    var els = (root || document).querySelectorAll('[data-ds-count]');
    if (!els.length) return;
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          animateCount(entry.target);
          io.unobserve(entry.target);
        }
      });
    }, { threshold: 0.5 });
    els.forEach(function (el) { io.observe(el); });
  }

  /* ========================================================================
     3. 3D TILT
     ======================================================================== */
  function initTilt(root) {
    if (REDUCED || !FINE_POINTER) return;
    var els = (root || document).querySelectorAll('[data-ds-tilt]');
    els.forEach(function (el) {
      if (el.__dsTilt) return;
      el.__dsTilt = true;
      var max = parseFloat(el.getAttribute('data-ds-tilt')) || 6;
      var move = rafThrottle(function (e) {
        var r = el.getBoundingClientRect();
        var px = (e.clientX - r.left) / r.width - 0.5;
        var py = (e.clientY - r.top) / r.height - 0.5;
        el.style.setProperty('--ds-ry', (px * max).toFixed(2) + 'deg');
        el.style.setProperty('--ds-rx', (-py * max).toFixed(2) + 'deg');
        el.style.setProperty('--ds-tz', '6px');
      });
      el.addEventListener('pointerenter', function () { el.classList.add('ds-tilting'); });
      el.addEventListener('pointermove', move);
      el.addEventListener('pointerleave', function () {
        el.classList.remove('ds-tilting');
        el.style.setProperty('--ds-rx', '0deg');
        el.style.setProperty('--ds-ry', '0deg');
        el.style.setProperty('--ds-tz', '0px');
      });
    });
  }

  /* ========================================================================
     4. MAGNETIC
     ======================================================================== */
  function initMagnetic(root) {
    if (REDUCED || !FINE_POINTER) return;
    var els = (root || document).querySelectorAll('[data-ds-magnetic]');
    els.forEach(function (el) {
      if (el.__dsMag) return;
      el.__dsMag = true;
      var strength = parseFloat(el.getAttribute('data-ds-magnetic')) || 0.3;
      var move = rafThrottle(function (e) {
        var r = el.getBoundingClientRect();
        var dx = (e.clientX - (r.left + r.width / 2)) * strength;
        var dy = (e.clientY - (r.top + r.height / 2)) * strength;
        el.style.transform = 'translate(' + dx.toFixed(1) + 'px,' + dy.toFixed(1) + 'px)';
      });
      el.addEventListener('pointermove', move);
      el.addEventListener('pointerleave', function () { el.style.transform = ''; });
    });
  }

  /* ========================================================================
     5. READING PROGRESS (article pages only)
     ======================================================================== */
  function initReadbar() {
    if (REDUCED) return;
    if (!document.body.classList.contains('template-article') &&
        !document.querySelector('.blog-post-content')) return;
    if (document.querySelector('.ds-readbar')) return;
    var bar = document.createElement('div');
    bar.className = 'ds-readbar';
    document.body.appendChild(bar);
    var update = rafThrottle(function () {
      var h = document.documentElement.scrollHeight - window.innerHeight;
      var p = h > 0 ? clamp(window.scrollY / h, 0, 1) : 0;
      bar.style.setProperty('--ds-progress', p);
    });
    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    update();
  }

  /* ========================================================================
     6. HEADER SCROLL STATE — adds .ds-scrolled to <html> past 40px
     (CSS in section files can react to html.ds-scrolled).
     ======================================================================== */
  function initHeaderScroll() {
    var root = document.documentElement;
    var update = rafThrottle(function () {
      root.classList.toggle('ds-scrolled', window.scrollY > 40);
    });
    window.addEventListener('scroll', update, { passive: true });
    update();
  }

  /* ---------- boot ---------- */
  function boot(root) {
    initReveal(root);
    initCounts(root);
    initTilt(root);
    initMagnetic(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      boot(document);
      initReadbar();
      initHeaderScroll();
    });
  } else {
    boot(document);
    initReadbar();
    initHeaderScroll();
  }

  /* Re-run when Shopify theme editor injects/reloads a section */
  document.addEventListener('shopify:section:load', function (e) { boot(e.target); });

  /* Expose for manual re-init if needed */
  window.dsElevate = boot;
})();
